import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { Toaster, toast } from "sonner";
import Backdrop from "@/theme/Backdrop";
import Button from "@/theme/Button";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { LOGO_CIRCLE } from "@/theme/tokens";

const LoginPage = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/admin/dashboard", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Signed in.");
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#060606" }}>
      <Backdrop />
      <Toaster theme="dark" position="bottom-right" />
      <div className="flex-1 flex items-center justify-center px-6 py-16 relative z-10">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md p-8 space-y-5"
          style={{
            background: "rgba(6,6,6,0.85)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "24px",
            backdropFilter: "blur(20px)",
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <img
              src={LOGO_CIRCLE}
              alt="Glitch Game Labs"
              className="w-12 h-12 rounded-full"
              style={{ background: "#fff" }}
            />
            <div>
              <h1
                className="font-black uppercase text-xl text-white"
                style={{
                  fontFamily: "'Azeret Mono', monospace",
                  letterSpacing: "-0.03em",
                }}
              >
                Admin
              </h1>
              <p className="text-[10px] tracking-[0.32em] uppercase text-white/50">
                Glitch Game Labs · Dashboard
              </p>
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold block mb-2">
              Email
            </label>
            <input
              data-testid="admin-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/15 text-white placeholder-white/30 focus:border-[#c8ff2a] focus:outline-none focus:ring-2 focus:ring-[#c8ff2a]/30"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold block mb-2">
              Password
            </label>
            <input
              data-testid="admin-password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-transparent border border-white/15 text-white placeholder-white/30 focus:border-[#c8ff2a] focus:outline-none focus:ring-2 focus:ring-[#c8ff2a]/30"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            />
          </div>

          <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full">
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Signing in…</>
            ) : (
              <><LogIn size={14} strokeWidth={3} /> Enter dashboard</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
